import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useQuery } from '@apollo/client';

import { ModalModel, ModalContext } from '@notino/react-styleguide';
import { resetDataLayer } from '@notino/web-tracking';

import { variantFragment_customerBenefits } from '../../../queries/__generated__/variantFragment';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { trackPageReload } from '../../utils';

import { BenefitsModal } from './components/BenefitsModal';
import { HowToLoyaltyModal } from './components/HowToLoyaltyDiscountModal';
import { messages } from './messages';
import { getCustomerBenefits } from './queries/__generated__/getCustomerBenefits';
import getCustomerBenefitsQuery from './queries/getCustomerBenefits.graphql';
import {
  CustomerDiscountBoldWrapper,
  CustomerDiscountWrapper,
  LoyaltyDiscountStyledWrapper,
  StyledDiamondIcon,
} from './styled';
import { getDiscountWithSymbol, subjectTypeMessages } from './utils';

interface ILoyaltyDiscount {
  customerBenefits: variantFragment_customerBenefits;
  // We need these props to track pageView of PD after modal close
  gtmPageReloadProps: Parameters<typeof trackPageReload>[0];
}

export const LoyaltyDiscount: React.FC<ILoyaltyDiscount> = ({
  customerBenefits,
  gtmPageReloadProps,
}) => {
  const newDesign = useNewPdDesignEnabled();
  const { data, loading } = useQuery<getCustomerBenefits>(
    getCustomerBenefitsQuery,
    {
      ssr: false,
    }
  );

  const { toggleModal, hideModal } = ModalContext.useModalContext();

  const handleClick = () => {
    toggleModal(
      data?.customerBenefits?.hasActiveBenefits ? (
        <BenefitsModal />
      ) : (
        <HowToLoyaltyModal />
      ),
      {
        noBorders: true,
        size: ModalModel.Sizes.large,
        showOverflow: true,
        onClose: () => {
          resetDataLayer();
          trackPageReload(gtmPageReloadProps);
          hideModal();
        },
      }
    );
  };

  const formattedBenefits = React.useMemo(
    () => ({
      availableDiscount: customerBenefits.availableDiscount && {
        value: getDiscountWithSymbol(
          customerBenefits.availableDiscount.discountType,
          customerBenefits.availableDiscount.value
        ),
      },
      customerDiscount: customerBenefits.customerDiscount && {
        value: getDiscountWithSymbol(
          customerBenefits.customerDiscount.discountType,
          customerBenefits.customerDiscount.value
        ),
      },
    }),
    [customerBenefits.availableDiscount, customerBenefits.customerDiscount]
  );

  if (
    !formattedBenefits.availableDiscount &&
    !formattedBenefits.customerDiscount
  ) {
    return null;
  }

  const isCustomerDiscount = Boolean(formattedBenefits.customerDiscount);

  return (
    <LoyaltyDiscountStyledWrapper
      onClick={handleClick}
      newDesign={newDesign}
      isCustomerDiscount={isCustomerDiscount}
      disabled={loading}
    >
      <>
        <StyledDiamondIcon width="24px" height="24px" />
        {customerBenefits.availableDiscount && (
          <FormattedMessage
            {...messages.possibleDiscount}
            values={{ value: formattedBenefits.availableDiscount.value }}
          />
        )}
        {customerBenefits.customerDiscount && (
          <span>
            <FormattedMessage
              {...messages.customerDiscount}
              values={{
                discountSubject:
                  customerBenefits.customerDiscount.discountSubject,
                subjectType: (
                  <FormattedMessage
                    {...subjectTypeMessages[
                      customerBenefits.customerDiscount.subjectType
                    ]}
                  />
                ),
                customerDiscountBold: (
                  <CustomerDiscountBoldWrapper>
                    <FormattedMessage
                      {...messages.customerDiscountBold}
                      values={{
                        value: formattedBenefits.customerDiscount.value,
                      }}
                    />
                  </CustomerDiscountBoldWrapper>
                ),
                more: (
                  <CustomerDiscountWrapper>
                    <FormattedMessage {...messages.moreDiscountInfo} />
                  </CustomerDiscountWrapper>
                ),
              }}
            />
          </span>
        )}
      </>
    </LoyaltyDiscountStyledWrapper>
  );
};
